import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { exportOfferToPDF, OfferData } from '@/lib/pdfExport';
import { 
  FileDown, 
  Pencil, 
  Trash2, 
  Search, 
  FileText,
  RefreshCw,
  Plus,
  Eye
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';

interface Offer {
  id: string;
  offer_number: string;
  customer_name: string;
  customer_address: string | null;
  customer_email: string | null;
  offer_date: string;
  valid_until: string | null;
  items: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
  }>;
  additional_content: string | null;
  notes: string | null;
  status: string;
  total_net: number | null;
  total_vat: number | null;
  total_gross: number | null;
  created_at: string;
}

interface OffersManagementProps {
  onEditOffer?: (offer: Offer) => void;
  onNewOffer?: () => void;
}

export function OffersManagement({ onEditOffer, onNewOffer }: OffersManagementProps) {
  const { toast } = useToast();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOffers((data || []) as any);
    } catch (error: any) {
      console.error('Error fetching offers:', error);
      toast({
        title: 'Fehler',
        description: 'Angebote konnten nicht geladen werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      const { error } = await supabase
        .from('offers')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setOffers(prev => prev.filter(o => o.id !== id));
      toast({
        title: 'Gelöscht',
        description: 'Angebot wurde erfolgreich gelöscht.',
      });
    } catch (error: any) {
      console.error('Error deleting offer:', error);
      toast({
        title: 'Fehler',
        description: 'Angebot konnte nicht gelöscht werden.',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async (offer: Offer) => {
    setExportingId(offer.id);
    try {
      const offerData: OfferData = {
        offerNumber: offer.offer_number,
        offerDate: new Date(offer.offer_date),
        validUntil: offer.valid_until ? new Date(offer.valid_until) : new Date(),
        customer: {
          name: offer.customer_name,
          address: offer.customer_address || '',
          email: offer.customer_email || '',
        },
        positions: offer.items || [],
        notes: offer.notes || undefined,
        additionalContent: offer.additional_content || undefined,
        taxRate: 19,
        isKleinunternehmer: true,
      };

      await exportOfferToPDF(offerData);
      
      toast({
        title: 'PDF erstellt',
        description: `Angebot ${offer.offer_number} wurde heruntergeladen.`,
      });
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Fehler',
        description: 'PDF konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    } finally {
      setExportingId(null);
    }
  };

  const formatCurrency = (amount: number | null): string => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Entwurf</Badge>;
      case 'sent':
        return <Badge className="bg-blue-500">Versendet</Badge>;
      case 'accepted':
        return <Badge className="bg-green-500">Angenommen</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Abgelehnt</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredOffers = offers.filter(offer => 
    offer.offer_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    offer.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (offer.customer_email && offer.customer_email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Card>
      <CardHeader className="border-b bg-slate-50">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Gespeicherte Angebote
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchOffers}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Aktualisieren
            </Button>
            {onNewOffer && (
              <Button size="sm" onClick={onNewOffer} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Neues Angebot
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Suchen nach Angebotsnummer, Kunde..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm ? 'Keine Angebote gefunden.' : 'Noch keine Angebote gespeichert.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nr.</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Gültig bis</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOffers.map((offer) => (
                  <TableRow key={offer.id}>
                    <TableCell className="font-medium">{offer.offer_number}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{offer.customer_name}</div>
                        {offer.customer_email && (
                          <div className="text-sm text-muted-foreground">{offer.customer_email}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(offer.offer_date)}</TableCell>
                    <TableCell>{offer.valid_until ? formatDate(offer.valid_until) : '-'}</TableCell>
                    <TableCell>{formatCurrency(offer.total_gross)}</TableCell>
                    <TableCell>{getStatusBadge(offer.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onEditOffer && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditOffer(offer)}
                            title="Bearbeiten"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleExport(offer)}
                          disabled={exportingId === offer.id}
                          title="PDF exportieren"
                        >
                          {exportingId === offer.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                          ) : (
                            <FileDown className="w-4 h-4" />
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Angebot löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Möchten Sie das Angebot {offer.offer_number} wirklich löschen? 
                                Diese Aktion kann nicht rückgängig gemacht werden.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(offer.id)}
                                className="bg-red-500 hover:bg-red-600"
                              >
                                {deletingId === offer.id ? 'Löschen...' : 'Löschen'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Summary */}
        {filteredOffers.length > 0 && (
          <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
            {filteredOffers.length} Angebot{filteredOffers.length !== 1 ? 'e' : ''} gefunden
          </div>
        )}
      </CardContent>
    </Card>
  );
}
